import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import grassDepotLogo from '@/assets/grass-depot-logo.png';

const Footer = () => {
  const { language, t } = useLanguage();

  const areasServed = [
    'Magnolia TX', 'The Woodlands TX', 'Conroe TX', 'Cypress TX',
    'Tomball TX', 'Spring TX', 'Willis TX', 'Montgomery County TX'
  ];

  return (
    <footer className="bg-secondary text-secondary-foreground py-20">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src={grassDepotLogo} 
                alt="The Grass Depot LLC"
                className="h-16 w-auto"
                width="94"
                height="64"
                loading="lazy"
              />
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="h-6 w-6 mt-1 flex-shrink-0 text-primary" />
                <p className="text-base">{t('company.address')}</p>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-6 w-6 flex-shrink-0 text-primary" />
                <a href="tel:9363200763" className="text-base hover:text-primary transition-colors font-medium">
                  {t('company.phone')}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-6 w-6 flex-shrink-0 text-primary" />
                <a href="mailto:thegrassdepotllc@gmail.com" className="text-base hover:text-primary transition-colors font-medium">
                  thegrassdepotllc@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-primary">
              {language === 'en' ? 'Products' : 'Productos'}
            </h3>
            <ul className="space-y-3 text-base">
              <li><Link to={`/${language}/grass`} className="hover:text-primary transition-colors font-medium">{t('nav.grass')}</Link></li>
              <li><Link to={`/${language}/rock`} className="hover:text-primary transition-colors font-medium">{t('nav.rock')}</Link></li>
              <li><Link to={`/${language}/mulch`} className="hover:text-primary transition-colors font-medium">{t('nav.mulch')}</Link></li>
              <li><Link to={`/${language}/soil`} className="hover:text-primary transition-colors font-medium">{t('nav.soil')}</Link></li>
              <li><Link to={`/${language}/sand`} className="hover:text-primary transition-colors font-medium">{t('nav.sand')}</Link></li>
              <li><Link to={`/${language}/services`} className="hover:text-primary transition-colors font-medium">{t('nav.services')}</Link></li>
            </ul>
          </div>

          {/* Areas Served */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-primary">{t('nav.areas')}</h3>
            <ul className="space-y-2 text-base">
              {areasServed.slice(0, 6).map((area) => (
                <li key={area} className="text-secondary-foreground/90 font-medium">{area}</li>
              ))}
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="font-bold text-lg mb-6 flex items-center text-primary">
              <Clock className="h-6 w-6 mr-3" />
              {t('footer.hours')}
            </h3>
            <div className="space-y-3 text-base">
              <p className="font-medium">{t('footer.hours.weekdays')}</p>
              <p className="font-medium">{t('footer.hours.saturday')}</p>
              <p className="font-medium">{t('footer.hours.sunday')}</p>
            </div>
            <div className="mt-6 pt-6 border-t border-secondary-foreground/20">
              <p className="text-base text-secondary-foreground/90 font-medium">
                {t('footer.serving')}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-secondary-foreground/20 mt-16 pt-10 flex flex-col md:flex-row justify-between items-center">
          <div className="flex flex-col items-center md:items-start">
            <p className="text-base text-secondary-foreground/90 font-medium mb-2">
              © {new Date().getFullYear()} {t('company.name')}. {language === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}
            </p>
            <p className="text-sm text-secondary-foreground/70">
              Developed by{' '}
              <a 
                href="https://rocwwa.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                ROC Worldwide Agency LLC
              </a>
              {' '}/{' '}
              SEO Optimized by{' '}
              <a 
                href="https://luisroc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Luis ROC
              </a>
            </p>
          </div>
          <div className="flex space-x-8 mt-6 md:mt-0 text-base">
            <Link to={language === 'en' ? '/en/privacy' : '/es/privacidad'} className="hover:text-primary transition-colors font-medium">
              {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
            </Link>
            <Link to={language === 'en' ? '/en/terms' : '/es/terminos'} className="hover:text-primary transition-colors font-medium">
              {language === 'en' ? 'Terms of Service' : 'Términos de Servicio'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;